<script lang="js">
import AppConfig from '@/shared/services/app_config';
import AuthModalMixin from '@/mixins/auth_modal';
import EventBus from '@/shared/services/event_bus';
import AbilityService from '@/shared/services/ability_service';
import Session from '@/shared/services/session';
import Flash from '@/shared/services/flash';
import { compact, truncate } from 'lodash-es';
import { initLiveUpdate, closeLiveUpdate } from '@/shared/helpers/message_bus';


import { useTheme } from 'vuetify';

import SidebarPanel from '@/components/sidebar/panel';
import MobileNavigation from '@/components/common/mobile_navigation';

const themeNameNormalized = (name) => ['dark', 'darkBlue'].includes(name) ? 'dark' : 'light';
const themeColorsApply = (theme, name, overrides) => {
  const colors = theme.themes.value[name].colors;

  Object.entries(overrides).forEach(([key, value]) => {
    if (value) { colors[key] = value; }
  });

  if (!overrides.anchor) { colors.anchor = colors.primary; }
  colors.appbar ||= colors.background;
  colors['appbar-scrolled'] ||= colors.appbar;
  colors.drawer ||= colors.surface;
  colors['thread-drawer'] ||= colors.background;
};

export default {
  components: { SidebarPanel, MobileNavigation },
  mixins: [AuthModalMixin],

  data() {
    return {pageError: null};
  },

  computed: {
    themeBackgroundStyle() {
      const style = {};

      if (AppConfig.theme.background_gradient?.light) {
        style['--loomio-theme-light-background-gradient'] = AppConfig.theme.background_gradient.light;
        style['--loomio-theme-light-background-foreground'] = 'rgb(var(--v-theme-on-background))';
      }

      if (AppConfig.theme.background_gradient?.dark) {
        style['--loomio-theme-dark-background-gradient'] = AppConfig.theme.background_gradient.dark;
        style['--loomio-theme-dark-background-foreground'] = 'rgb(var(--v-theme-on-background))';
      }

      return style;
    },
  },

  created() {
    const theme = useTheme();

    themeColorsApply(theme, 'light', AppConfig.theme.light);
    themeColorsApply(theme, 'dark', AppConfig.theme.dark);

    if (Session.user().experiences.theme != null) {
      theme.change(themeNameNormalized(Session.user().experiences['theme']));
    } else {
      const defaultTheme = (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? AppConfig.theme.default_dark_theme : AppConfig.theme.default_light_theme;
      theme.change(themeNameNormalized(defaultTheme));
    }
  },

  mounted() {
    if (Session.isSignedIn()) { initLiveUpdate(); }
    EventBus.$on('currentComponent',     this.setCurrentComponent);
    EventBus.$on('openAuthModal',     () => this.openAuthModal());
    EventBus.$on('pageError', error => { return this.pageError = error; });
    EventBus.$on('signedIn',          () => { this.pageError = null; initLiveUpdate(); });
    Flash.fromServer(AppConfig.flash);
  },

  destroyed() {
    closeLiveUpdate();
  },

  watch: {
    '$route'() {
      if (!Session.isSignedIn() && this.shouldForceSignIn()) { this.openAuthModal(true); }
      this.pageError = null;
    }
  },

  methods: {
    setCurrentComponent(options) {
      this.pageError = null;
      const title = truncate(options.title || this.$t(options.titleKey), {length: 300});
      document.querySelector('title').text = compact([title, AppConfig.theme.site_name]).join(' | ');

      AppConfig.currentGroup      = options.group;
      AppConfig.currentDiscussion = options.discussion;
      AppConfig.currentPoll       = options.poll;
    },

    shouldForceSignIn(options = {}) {
      if (this.$route.query.sign_in) { return true; }
      if ((AppConfig.pendingIdentity || {}).identity_type != null) { return true; }
      if (Session.isSignedIn()) { return false; }

      switch (this.$route.path) {
        case '/email_preferences': return true;
        case '/dashboard': return true;
        case '/notifications': return true;
        case '/inbox':
        case '/profile':
        case '/polls':
        case '/p/new':
        case '/g/new': return true;
      }
    }
  }
};

</script>

<template lang="pug">
v-app.app-is-booted(:style="themeBackgroundStyle")
  system-notice
  sidebar-panel
  navbar
  mobile-navigation
  router-view(v-if="!pageError")
  common-error(v-if="pageError" :error="pageError")
  v-spacer
  modal-launcher
  common-flash
</template>

<style>
@import '@/css/roboto.css';
@import '@/css/thumbicons.css';
.lmo-disabled-form {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  border-radius: 6px;
  opacity: 0.5;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

@media print {
  header,
  .lmo-no-print,
  .md-sidenav-left.lmo-no-print,
  .md-dialog-backdrop,
  .md-open-menu-container,
  .sr-only {
    display: none !important;
  }
  .lmo-one-column-layout,
  .lmo-two-column-layout {
    padding-top: 0 !important;
  }
  .lmo-print-only {
    display: block !important;
  }
  .lmo-main-background {
    position: static !important;
  }
  .lmo-column-left,
  .lmo-column-right {
    width: 100% !important;
    float: none !important;
  }
  .lmo-sidebar-and-main-container {
    display: block;
    overflow: visible !important;
  }
  .topic-page .group-theme__header--compact {
    margin: 0 0 15px 0;
  }
  .topic-page a[href]:after {
    content: "" !important;
  }
}
.v-main :is(.v-card, .v-sheet):not(:is(.v-card, .v-sheet) :is(.v-card, .v-sheet)) {
  box-shadow: none !important;
}

.v-application.v-theme--light {
  background: var(--loomio-theme-light-background-gradient, rgb(var(--v-theme-background)));
  background-attachment: fixed;
}

.v-application.v-theme--dark {
  background: var(--loomio-theme-dark-background-gradient, rgb(var(--v-theme-background)));
  background-attachment: fixed;
}

.v-theme--light .v-main :is(.v-card, .v-sheet):not(:is(.v-card, .v-sheet) :is(.v-card, .v-sheet)) {
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

@layer vuetify-core.reset {
  ul, ol, figure, details, summary {
    padding: 0;
    margin: 0;
  }
  h1, h2, h3, h4, h5, h6, p {
    margin: 0;
  }
}
.underline-on-hover:hover {
  text-decoration: underline;
}

.v-card.group-form > .v-card__content {
  overflow: visible !important;
}

.v-card.discussion-form,
.v-card.discussion-template-form,
.v-card.discussion-template-form .v-card-item__content,
.v-card.discussion-form .v-card-item__content,
.v-card.poll-template-form-card,
.v-card.poll-template-form-card .v-card-item__content,
.v-card.poll-common-form-card,
.v-card.poll-common-form-card .v-card-item__content,
.v-card.poll-common-modal,
.v-card.poll-common-modal .v-card-item__content {
  overflow: visible !important;
}

.text-on-surface {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

h1:focus, h2:focus, h3:focus, h4:focus, h5:focus, h6:focus {
  outline: 0;
}

.max-width-320 {
  max-width: 320px !important;
}

.max-width-400 {
  max-width: 400px !important;
}

.max-width-640 {
  max-width: 640px !important;
}

.max-width-800 {
  max-width: 800px !important;
}

.max-width-900 {
  max-width: 900px !important;
}

.max-width-1024 {
  max-width: 1024px !important;
}

@media print {
  .lmo-no-print {
    display: none !important;
  }
}
</style>
